import { gql } from '@apollo/client';
import { useEventContext } from './useEventContext';
import { useCallback, useEffect, useState } from 'react';
import { CmsEvent, EventResponse } from '../models/EventResult';
import { useIntl } from 'react-intl';
import { findEventDetails } from '../reducer/EventsReducer';
import { cmsGraphql } from '@xolv-mybrightlink/common';
import { toast } from 'react-toastify';
import { useFetchBookmarkedEvents } from './useFetchBookmarkedEvents';

const GET_EVENT_DETAILS = gql`
  query EventById($id: ID!) {
    event(id: $id, idType: DATABASE_ID) {
      databaseId
      title(format: RENDERED)
      slug
      eventsCategories {
        nodes {
          name
          databaseId
          slug
        }
      }
      venue {
        address
        title(format: RENDERED)
      }
      featuredImage {
        node {
          mediaDetails {
            sizes {
              sourceUrl
              name
            }
          }
        }
      }
      startDate
      endDate
      allDay
      cost
      content(format: RENDERED)
      tags {
        nodes {
          databaseId
          name
        }
      }
      organizers {
        nodes {
          title
        }
      }
    }
  }
`;

export const useFetchEventDetails = (databaseId?: string) => {
  const [state, dispatch] = useEventContext();
  const fetchBookmarkedEvents = useFetchBookmarkedEvents();
  const [data, setData] = useState<'loading' | 'error' | CmsEvent>('loading');
  const intl = useIntl();
  const fetch = useCallback(async () => {
    if (!databaseId || state.initial) {
      return setData('loading');
    } else {
      const parsedDatabaseId = Number(databaseId);
      const existing = findEventDetails(state.eventDetails, parsedDatabaseId);
      if (existing) {
        setData(existing.data);
      } else {
        dispatch({ type: 'event-fetching', databaseId: parsedDatabaseId });
        try {
          const result = await cmsGraphql.query<EventResponse>({
            query: GET_EVENT_DETAILS,
            variables: { id: databaseId },
          });
          const [event] = await fetchBookmarkedEvents([result.data.event]);
          dispatch({ type: 'event-fetched', event });
        } catch {
          toast(intl.formatMessage({ id: 'common.error.message' }), {
            toastId: 'common.error.message',
          });
          dispatch({ type: 'event-error', databaseId: parsedDatabaseId });
        }
      }
    }
  }, [databaseId, dispatch, fetchBookmarkedEvents, intl, state]);

  useEffect(() => {
    fetch();
  }, [fetch]);
  return data;
};
